import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Viewer from "@/components/Viewer";
import { StatusBadge, Tag } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  ArrowUp, ArrowDown, Plus, X, BookmarkSimple, Database, Archive, Trash,
  MagnifyingGlass, Tag as TagIcon, ClockCounterClockwise, UserCircle,
} from "@phosphor-icons/react";

const PRIORITIES = ["high", "medium", "low"];
const TYPES = ["Research", "Policy", "Commentary", "Data", "Event", "Other"];
const RECOMMENDATIONS = ["Feature", "Include", "Monitor", "Discard"];

const DECISIONS = [
  { key: "editorial", label: "Editorial Folder", icon: BookmarkSimple, cls: "bg-emerald-600 hover:bg-emerald-500" },
  { key: "working", label: "Reference DB", icon: Database, cls: "bg-blue-600 hover:bg-blue-500" },
  { key: "archive", label: "Archive", icon: Archive, cls: "bg-amber-600 hover:bg-amber-500" },
];

const ACTION_DOT = {
  created: "bg-slate-400",
  assignment: "bg-purple-400",
  vote: "bg-emerald-400",
  annotation: "bg-blue-400",
  decision: "bg-amber-400",
};

const HISTORY_FILTERS = [
  { key: "all", label: "All" },
  { key: "vote", label: "Votes" },
  { key: "annotation", label: "Notes" },
  { key: "assignment", label: "Assignments" },
  { key: "decision", label: "Decisions" },
];

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

function TagEditor({ label, kind, values, onAdd, onRemove, testid }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {values.map((v) => (
          <span key={v} className="group">
            <Tag kind={kind} data-testid={`${testid}-tag-${v}`}>
              {v}
              <button onClick={() => onRemove(v)} data-testid={`${testid}-remove-${v}`} className="ml-1 opacity-60 hover:opacity-100"><X size={10} weight="bold" /></button>
            </Tag>
          </span>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onAdd(val.trim()); setVal(""); } }}
          placeholder="Add & press Enter" className="h-8 text-xs bg-[#0B0E14] border-dashed border-slate-600" />
        <Button size="sm" variant="ghost" data-testid={`${testid}-add`} className="h-8 px-2 hover:bg-white/5"
          onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}><Plus size={14} /></Button>
      </div>
    </div>
  );
}

function Workspace({ entry, onChanged }) {
  const { user } = useAuth();
  const [tax, setTax] = useState({});
  const [recommendation, setRecommendation] = useState("");
  const [histFilter, setHistFilter] = useState("all");

  useEffect(() => {
    setTax({
      priority: entry.priority || "",
      entry_type: entry.entry_type || "",
      recommendation: entry.recommendation || "",
      keywords: entry.keywords || [],
      categories: entry.categories || [],
      editor_notes: entry.editor_notes || "",
    });
    setRecommendation(entry.recommendation || "");
  }, [entry.id]);

  const myVote = entry.votes.find((v) => v.user_id === user.id)?.value;

  const { data: editors = [] } = useQuery({
    queryKey: ["editors"],
    queryFn: async () => (await api.get("/editors")).data,
    enabled: user.role === "admin",
  });

  const assign = async (editorId) => {
    try {
      await api.patch(`/entries/${entry.id}/assign`, { editor_id: editorId === "__none__" ? null : editorId });
      onChanged();
    } catch { toast.error("Assignment failed"); }
  };

  const saveTaxonomy = async (patch) => {
    const next = { ...tax, ...patch };
    setTax(next);
    try {
      await api.patch(`/entries/${entry.id}/taxonomy`, patch);
      onChanged();
    } catch { toast.error("Failed to save"); }
  };

  const vote = async (value) => {
    try { await api.post(`/entries/${entry.id}/vote`, { value }); onChanged(); }
    catch { toast.error("Vote failed"); }
  };

  const decide = async (decision) => {
    try {
      await api.post(`/entries/${entry.id}/decision`, { decision, recommendation });
      toast.success(`Routed to ${decision === "working" ? "Reference DB" : decision}`);
      onChanged(true);
    } catch { toast.error("Decision failed"); }
  };

  const del = async () => {
    try { await api.delete(`/entries/${entry.id}`); toast.success("Entry deleted"); onChanged(true); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6" data-testid="workspace">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Editorial Workspace</p>
        <StatusBadge status={entry.status} />
      </div>

      {/* Assignment */}
      <div className="bg-[#0B0E14] border border-white/5 rounded-lg p-4">
        <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Assigned Reviewer</Label>
        {user.role === "admin" ? (
          <Select value={entry.assigned_to || "__none__"} onValueChange={assign}>
            <SelectTrigger data-testid="assign-select" className="mt-2 h-8 text-xs bg-[#131722] border-white/10"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {editors.map((ed) => <SelectItem key={ed.id} value={ed.id}>{ed.name} · {ed.role}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="mt-2 flex items-center gap-2 text-sm" data-testid="assigned-name">
            <UserCircle size={18} weight="duotone" className={entry.assigned_to_name ? "text-blue-400" : "text-slate-600"} />
            <span className={entry.assigned_to_name ? "text-slate-200" : "text-slate-500"}>{entry.assigned_to_name || "Unassigned"}</span>
          </div>
        )}
      </div>

      {/* Voting */}
      <div className="bg-[#0B0E14] border border-white/5 rounded-lg p-4">
        <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Editorial Vote</Label>
        <div className="flex items-center gap-3 mt-3">
          <button data-testid="vote-up" onClick={() => vote(1)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md border transition-colors duration-200 ${myVote === 1 ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
            <ArrowUp size={16} weight="bold" /> <span className="font-mono text-sm" data-testid="vote-up-count">{entry.vote_up}</span>
          </button>
          <button data-testid="vote-down" onClick={() => vote(-1)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md border transition-colors duration-200 ${myVote === -1 ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
            <ArrowDown size={16} weight="bold" /> <span className="font-mono text-sm" data-testid="vote-down-count">{entry.vote_down}</span>
          </button>
          <div className="ml-auto text-right">
            <div className="font-mono text-lg font-bold" data-testid="vote-score">{entry.vote_score > 0 ? "+" : ""}{entry.vote_score}</div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">tally</div>
          </div>
        </div>
        {entry.votes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {entry.votes.map((v) => (
              <span key={v.user_id} className={`font-mono text-[10px] px-2 py-0.5 rounded border ${v.value === 1 ? "text-emerald-400 border-emerald-500/20 bg-emerald-500/5" : "text-red-400 border-red-500/20 bg-red-500/5"}`}>
                {v.name} {v.value === 1 ? "▲" : "▼"}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Taxonomy */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Priority</Label>
            <Select value={tax.priority || ""} onValueChange={(v) => saveTaxonomy({ priority: v })}>
              <SelectTrigger data-testid="tax-priority" className="mt-2 h-8 text-xs bg-[#0B0E14] border-white/10"><SelectValue placeholder="Set" /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Type</Label>
            <Select value={tax.entry_type || ""} onValueChange={(v) => saveTaxonomy({ entry_type: v })}>
              <SelectTrigger data-testid="tax-type" className="mt-2 h-8 text-xs bg-[#0B0E14] border-white/10"><SelectValue placeholder="Set" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <TagEditor label="Keywords" kind="keywords" testid="tax-keywords" values={tax.keywords || []}
          onAdd={(v) => saveTaxonomy({ keywords: [...(tax.keywords || []), v] })}
          onRemove={(v) => saveTaxonomy({ keywords: (tax.keywords || []).filter((x) => x !== v) })} />

        <TagEditor label="Additional Categories" kind="category" testid="tax-categories" values={tax.categories || []}
          onAdd={(v) => saveTaxonomy({ categories: [...(tax.categories || []), v] })}
          onRemove={(v) => saveTaxonomy({ categories: (tax.categories || []).filter((x) => x !== v) })} />

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Editorial Notes</Label>
          <Textarea data-testid="editor-notes" value={tax.editor_notes || ""} rows={3}
            onChange={(e) => setTax({ ...tax, editor_notes: e.target.value })}
            onBlur={() => saveTaxonomy({ editor_notes: tax.editor_notes })}
            className="mt-2 text-sm bg-[#0B0E14] border-white/10" placeholder="Editorial analysis…" />
        </div>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Recommendation</Label>
          <Select value={recommendation} onValueChange={(v) => { setRecommendation(v); saveTaxonomy({ recommendation: v }); }}>
            <SelectTrigger data-testid="tax-recommendation" className="mt-2 h-8 text-xs bg-[#0B0E14] border-white/10"><SelectValue placeholder="Set recommendation" /></SelectTrigger>
            <SelectContent>{RECOMMENDATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Decision */}
      <div className="border-t border-white/5 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-3">Final Decision</p>
        <div className="grid grid-cols-1 gap-2">
          {DECISIONS.map((d) => (
            <Button key={d.key} data-testid={`decision-${d.key}`} onClick={() => decide(d.key)}
              className={`justify-start gap-2 rounded-full text-white transition-colors duration-200 ${d.cls}`}>
              <d.icon size={16} weight="duotone" /> Save to {d.label}
            </Button>
          ))}
          <Button variant="outline" data-testid="decision-deleted" onClick={del}
            className="justify-start gap-2 rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors duration-200">
            <Trash size={16} weight="duotone" /> Delete
          </Button>
        </div>
      </div>

      {/* Decision History / Audit Trail */}
      <div className="border-t border-white/5 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
          <ClockCounterClockwise size={13} weight="duotone" /> Decision History
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {HISTORY_FILTERS.map((f) => (
            <button key={f.key} data-testid={`history-filter-${f.key}`} onClick={() => setHistFilter(f.key)}
              className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition-colors duration-200 ${histFilter === f.key ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "text-slate-500 border-white/10 hover:text-slate-300 hover:bg-white/5"}`}>
              {f.label}
            </button>
          ))}
        </div>
        {(() => {
          const events = [...(entry.activity || [])].reverse()
            .filter((a) => histFilter === "all" ? true : a.action === histFilter);
          if (events.length === 0)
            return <p className="text-xs text-slate-500" data-testid="activity-empty">No {histFilter === "all" ? "" : histFilter + " "}activity yet.</p>;
          return (
            <div className="space-y-3" data-testid="activity-log">
              {events.map((a) => (
                <div key={a.id} data-testid={`activity-${a.id}`} className="relative pl-4 border-l border-white/10">
                  <span className={`absolute -left-[3px] top-1.5 w-1.5 h-1.5 rounded-full ${ACTION_DOT[a.action] || "bg-slate-500"}`} />
                  <div className="text-xs text-slate-200">{a.detail}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-0.5">{a.by_name} · {relTime(a.at)}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default function Editorial() {
  const location = useLocation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(location.state?.entryId || null);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);

  const { data: entries = [], refetch: refetchList } = useQuery({
    queryKey: ["review-input", q, mine],
    queryFn: async () => (await api.get("/entries", { params: { status: "input", q: q || undefined, assigned: mine ? "me" : undefined } })).data,
  });

  const { data: selected, refetch: refetchEntry } = useQuery({
    queryKey: ["review-entry", selectedId],
    queryFn: async () => (await api.get(`/entries/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!selectedId && entries.length > 0) setSelectedId(entries[0].id);
  }, [entries, selectedId]);

  const handleChanged = useCallback((routed) => {
    refetchList();
    qc.invalidateQueries({ queryKey: ["stats"] });
    if (routed) { setSelectedId(null); }
    else { refetchEntry(); }
  }, [refetchList, refetchEntry, qc]);

  return (
    <div className="h-full flex overflow-hidden">
      {/* Left: queue */}
      <div className="w-72 shrink-0 border-r border-white/5 flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-white/5">
          <h2 className="text-sm font-semibold tracking-tight">Review Queue</h2>
          <span data-testid="queue-count" className="ml-2 font-mono text-xs text-slate-500">{entries.length}</span>
        </div>
        <div className="p-3 border-b border-white/5 space-y-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="queue-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 h-8 text-xs bg-[#131722] border-white/10" />
          </div>
          <div className="flex gap-1 bg-[#131722] border border-white/5 rounded-md p-0.5">
            <button data-testid="queue-filter-all" onClick={() => setMine(false)}
              className={`flex-1 text-[11px] font-mono uppercase tracking-wider py-1 rounded transition-colors duration-200 ${!mine ? "bg-blue-500/20 text-blue-300" : "text-slate-500 hover:text-slate-300"}`}>All</button>
            <button data-testid="queue-filter-mine" onClick={() => setMine(true)}
              className={`flex-1 text-[11px] font-mono uppercase tracking-wider py-1 rounded transition-colors duration-200 ${mine ? "bg-blue-500/20 text-blue-300" : "text-slate-500 hover:text-slate-300"}`}>Assigned to me</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {entries.length === 0 && <p className="text-center text-xs text-slate-500 py-10">Queue is empty</p>}
          {entries.map((e) => (
            <button key={e.id} data-testid={`queue-item-${e.id}`} onClick={() => setSelectedId(e.id)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors duration-200 ${selectedId === e.id ? "bg-blue-500/10 border-l-2 border-l-blue-500" : "hover:bg-white/5 border-l-2 border-l-transparent"}`}>
              <div className="text-sm font-medium truncate">{e.title}</div>
              <div className="flex items-center gap-2 mt-1.5">
                {e.priority && <Tag kind="priority">{e.priority}</Tag>}
                {e.assigned_to_name && <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-purple-400"><UserCircle size={11} weight="fill" />{e.assigned_to_name.split(" ")[0]}</span>}
                <span className="font-mono text-[10px] text-slate-500 ml-auto">▲{e.vote_up} ▼{e.vote_down}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Center + Right: resizable */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-w-0">
        <ResizablePanel defaultSize={60} minSize={35} className="min-w-0">
          <Viewer entry={selected} />
        </ResizablePanel>
        <ResizableHandle withHandle data-testid="viewer-resize-handle" />
        <ResizablePanel defaultSize={40} minSize={26} maxSize={60} className="min-w-0">
          {selected ? <Workspace entry={selected} onChanged={handleChanged} />
            : <div className="h-full flex items-center justify-center text-slate-500 text-sm p-6 text-center">
                <div><TagIcon size={40} weight="thin" className="mx-auto mb-3" />Select an entry to begin editorial analysis</div>
              </div>}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
